import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { API } from "../lib/api";
import type { PublicOrderAccess } from "./Carte/components/orderForm.api";
import { buildPublicCarteApiUrl, DEFAULT_PUBLIC_CARTE_ID } from "../lib/publicCarte";
import { CartePublicPage } from "./CartePublicPage";
import { ROUTES } from "../routes";

interface PublicWine {
    id: string;
    type: string;
    name: string;
    mil: string;
    dom: string;
    prix: string;
    desc: string;
    highlighted: boolean;
}

interface PublicCarteResponse {
    maison: string;
    wines: PublicWine[];
    brand: {
      logo: string | null;
      bgColor: string;
      textColor: string;
      cardColor: string;
      feeder?: any;
    };
}

// Standalone, unauthenticated public carte. Unlike /carte/public (which
// previews the currently-logged-in caviste's in-progress state), this route
// fetches everything itself from the public API so a client who received
// the link by email -- and has no VinIA session at all -- can actually see
// the wine list.
export function PublicCartePage() {
        const { publicCarteHash } = useParams<{ publicCarteHash: string }>();
        const location = useLocation();
        const navigate = useNavigate();
        const [data, setData] = useState<PublicCarteResponse | null>(null);
        const [status, setStatus] = useState<"loading" | "ready" | "not-found" | "error">("loading");
        const searchParams = new URLSearchParams(location.search);
        const publicOrderAccess: PublicOrderAccess | undefined = searchParams.get("customerHash")?.trim()
                ? {
                        customerHash: searchParams.get("customerHash")!.trim(),
                        userId: searchParams.get("userId")?.trim() || undefined,
                        carteId: searchParams.get("carteId")?.trim() || DEFAULT_PUBLIC_CARTE_ID,
                }
                : publicCarteHash
                        ? { customerHash: publicCarteHash }
                        : undefined;

        useEffect(() => {
                let cancelled = false;

                async function load() {
                        const customerHash = searchParams.get("customerHash")?.trim() || "";
                        const userId = searchParams.get("userId")?.trim() || "";
                        const carteId = searchParams.get("carteId")?.trim() || DEFAULT_PUBLIC_CARTE_ID;
                        const hasQueryAccess = searchParams.has("customerHash") || searchParams.has("userId") || searchParams.has("carteId");

                        setStatus("loading");
                        try {
                                const response = hasQueryAccess
                                        ? !customerHash
                                                ? null
                                                : userId
                                                        ? await fetch(
                                                                buildPublicCarteApiUrl(API || "", {
                                                                        customerHash,
                                                                        userId,
                                                                        carteId,
                                                                })
                                                        )
                                                        : await fetch(`${API}/api/public/carte/${encodeURIComponent(customerHash)}`)
                                        : publicCarteHash
                                                ? await fetch(`${API}/api/public/carte/${encodeURIComponent(publicCarteHash)}`)
                                                : null;

                                if (cancelled) return;
                                if (!response) {
                                        setStatus("not-found");
                                        return;
                                }
                                if (response.status === 404) {
                                        setStatus("not-found");
                                        return;
                                }
                                if (!response.ok) {
                                        setStatus("error");
                                        return;
                                }
                                setData(await response.json());
                                setStatus("ready");
                        } catch {
                                if (!cancelled) setStatus("error");
                        }
                }

                load();
                return () => {
                        cancelled = true;
                };
        }, [location.search, publicCarteHash]);

  if (status === "loading") {
                return (
                        <main className="flex flex-1 items-center justify-center" style={{ background: "#2A0E18", color: "#FFF6EE" }}>
                                <div className="text-[14px] opacity-70">Chargement de la carte…</div>
                        </main>
                );
  }

        if (status === "not-found" || status === "error" || !data) {
                return (
                        <main className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center" style={{ background: "#2A0E18", color: "#FFF6EE" }}>
                                <div className="text-[20px] font-semibold">Carte introuvable</div>
                                <div className="max-w-[360px] text-[14px] opacity-70">
                                        Ce lien ne correspond à aucune carte publiée, ou le client n&apos;existe plus.
                                </div>
                                <button
                                        onClick={() => navigate(ROUTES.VINAI)}
                                        className="mt-2 rounded-[12px] border border-white/30 bg-white/10 px-4 py-2 text-[13px] font-semibold"
                                >
                                        Retour à VinIA
                                </button>
                        </main>
                );
    }

        return (
                <CartePublicPage
                        wines={data.wines.map((wine) => ({ ...wine, visible: true }))}
                        maison={data.maison}
                        updated=""
                        brand={{
                                logo: data.brand?.logo ?? null,
                                bgColor: data.brand?.bgColor || "#0A0908",
                                textColor: data.brand?.textColor || "#F0EDE8",
                                cardColor: data.brand?.cardColor || "#141210",
                                feeder: data.brand?.feeder,
                        }}
                        publicOrderAccess={publicOrderAccess}
                />
        );
}
